import React, { useState, useRef } from 'react';
import DrawingOptions from 'components/quote_builder_widget/DrawingOptions';
import CustomerContactPopup from 'components/quote_builder_widget/shared/CustomerContactPopup';
import CustomerConfirmationPopup from 'components/quote_builder_widget/shared/CustomerConfirmationPopup';
import askedCustomerContactFields from 'components/quote_builder_widget/shared/customerContactFields';
import { PostRequest } from 'components/common/index.js';

const SUCCESS_DISPLAY_DURATION_MILLISECONDS = 3000;

// The confirmed customer token a right 2FA code answers (QuoteBuilderWidget::CustomerConfirmationService): posted with
// the customer's next requests, it spares them another code for two weeks. Kept in this browser, per widget; storage can
// be unavailable (a private window, blocked site data), and the customer is then asked for a code again.
const confirmedCustomerStorageKey = (widgetIdentifier) => `quote_builder_widget_confirmed_customer:${widgetIdentifier}`;

const storedConfirmedCustomerToken = (widgetIdentifier) => {
  try {
    return window.localStorage.getItem(confirmedCustomerStorageKey(widgetIdentifier));
  } catch (exception) {
    return null;
  }
};

const storeConfirmedCustomerToken = (widgetIdentifier, token) => {
  if (!token) {
    return;
  }
  try {
    window.localStorage.setItem(confirmedCustomerStorageKey(widgetIdentifier), token);
  } catch (exception) {
    // Not kept: the customer's next request asks for a code again.
  }
};

const QuoteBuilderForm = ({ theme, id, setId, drawing, unitOption = 'both', widgetIdentifier, widgetFormToken, onTokenRefresh,
  askCustomerEmail = false, askCustomerPhone = false }) => {
  const formRef = useRef(null);
  const [buttonState, setButtonState] = useState('ready');
  const [errorMessage, setErrorMessage] = useState(null);
  const retryCountRef = useRef(0);
  const [cartonMaterialUserPriceId, setCartonMaterialUserPriceId] = useState(null);
  const [unit, setUnit] = useState(unitOption === 'in' ? 'in' : 'mm');
  // The contact details the owner asks for are not part of the form: Submit asks for them in a popup, which sends. The
  // server's answer replaces them when the owner changed them after this page loaded.
  const [askedContact, setAskedContact] = useState({ askCustomerEmail, askCustomerPhone });
  const contactFields = askedCustomerContactFields(askedContact);
  const [contactPopupOpen, setContactPopupOpen] = useState(false);
  // For the request's answer, which arrives in handlers made before the popup opened or closed.
  const contactPopupOpenRef = useRef(false);
  const [contactErrorMessage, setContactErrorMessage] = useState(null);
  // The contact details last sent: posted with the request (and its retry with a fresh form token), and shown again
  // when the customer asks for another quote.
  const customerContactRef = useRef({});
  const [confirmation, setConfirmation] = useState(null);
  // Posted again with a request submitted after the popup was closed, so a code already sent is not sent again.
  const pendingConfirmationTokenRef = useRef(null);

  const DrawingMaterialChooser = theme.DrawingMaterialChooser;
  const PrintingColorChooser = theme.PrintingColorChooser;
  const HotStampingChooser = theme.HotStampingChooser;
  const SubmitButton = theme.SubmitButton;

  const showSuccess = () => {
    setButtonState('success');
    setTimeout(() => {
      setButtonState('ready');
    }, SUCCESS_DISPLAY_DURATION_MILLISECONDS);
  };

  const openContactPopup = () => {
    contactPopupOpenRef.current = true;
    setContactPopupOpen(true);
  };

  const closeContactPopup = () => {
    contactPopupOpenRef.current = false;
    setContactPopupOpen(false);
  };

  const showRequestError = (responseData) => {
    closeContactPopup();
    if (responseData?.rules && responseData.rules.length > 0) {
      setErrorMessage(responseData.rules[0].description);
    } else if (responseData?.error) {
      setErrorMessage(responseData.error);
    }
  };

  // The contact details, or the code they get, were refused: the customer corrects them in the popup, or -- with no
  // popup open -- reads why under the form.
  const showContactError = (responseData) => {
    if (!contactPopupOpenRef.current) {
      showRequestError(responseData?.error ? responseData : { error: t('quote_builder_widget.unexpected_error') });
      return;
    }
    setContactErrorMessage(responseData?.error || t('quote_builder_widget.unexpected_error'));
  };

  const submitWithToken = (token) => {
    const catalogName = drawing?.catalog_name;
    if (!formRef.current || !catalogName) {
      return;
    }

    const formData = new FormData(formRef.current);
    const entries = Object.fromEntries(formData.entries());

    // Separate drawing options from other form fields
    // drawing.option_names is a string array from the API (e.g., ['a', 'b', 'c'])
    const drawingOptionNames = drawing?.option_names || [];
    const data = {
      drawing_options: {},
      widget_form_token: token,
      confirmed_customer_token: storedConfirmedCustomerToken(widgetIdentifier),
      pending_confirmation_token: pendingConfirmationTokenRef.current
    };

    for (const [key, value] of Object.entries(entries)) {
      if (drawingOptionNames.includes(key)) {
        data.drawing_options[key] = value;
      } else if (!key.startsWith('__')) {
        // Skip checkbox helper fields (prefixed with __)
        data[key] = value;
      }
    }
    contactFields.forEach((field) => {
      data[field.name] = customerContactRef.current[field.name] || '';
    });

    PostRequest({
      url: `/widget/quote-builder/${widgetIdentifier}/${catalogName}/build-quote`,
      data: data,
      keysLeftOutOfTracking: ['confirmed_customer_token', 'pending_confirmation_token', 'customer_email', 'customer_phone'],
      callback: (responseData) => {
        retryCountRef.current = 0;
        closeContactPopup();
        if (responseData?.is === 'confirmation_required') {
          setButtonState('ready');
          pendingConfirmationTokenRef.current = responseData.pending_confirmation_token;
          setConfirmation({
            pendingConfirmationToken: responseData.pending_confirmation_token,
            resendAvailableInSeconds: responseData.resend_available_in_seconds,
            customerTwoFactor: responseData.customer_two_factor,
            customerDestination: responseData.customer_destination,
          });
          return;
        }
        showSuccess();
      },
      400: (responseData) => {
        retryCountRef.current = 0;
        setButtonState('ready');
        if (responseData?.customer_contact_invalid) {
          setAskedContact({ askCustomerEmail: responseData.ask_customer_email, askCustomerPhone: responseData.ask_customer_phone });
          openContactPopup();
          setContactErrorMessage(responseData.error);
        } else {
          showRequestError(responseData);
        }
      },
      403: (responseData) => {
        // Token was invalid/expired - server returns a fresh token
        if (responseData.widget_form_token && onTokenRefresh && retryCountRef.current < 1) {
          retryCountRef.current += 1;
          onTokenRefresh(responseData.widget_form_token);
          // Retry with the new token (form values are preserved)
          submitWithToken(responseData.widget_form_token);
        } else {
          retryCountRef.current = 0;
          setButtonState('ready');
          showRequestError({ error: t('quote_builder_widget.session_expired') });
        }
      },
      404: (responseData) => {
        retryCountRef.current = 0;
        setButtonState('ready');
        showRequestError(responseData);
      },
      // The code could not be sent to the contact details (too many codes, or the SMS or email failed).
      429: (responseData) => {
        retryCountRef.current = 0;
        setButtonState('ready');
        showContactError(responseData);
      },
      503: (responseData) => {
        retryCountRef.current = 0;
        setButtonState('ready');
        showContactError(responseData);
      },
      500: () => {
        retryCountRef.current = 0;
        setButtonState('ready');
        showRequestError({ error: t('quote_builder_widget.unexpected_error') });
      }
    }).catch(() => {
      retryCountRef.current = 0;
      setButtonState('ready');
    });
  };

  const handleSubmit = () => {
    if (buttonState !== 'ready' || !formRef.current || !drawing?.catalog_name) {
      return;
    }

    setErrorMessage(null);
    if (contactFields.length > 0) {
      setContactErrorMessage(null);
      openContactPopup();
      return;
    }
    setButtonState('submitting');
    submitWithToken(widgetFormToken);
  };

  const sendWithContact = (values) => {
    customerContactRef.current = values;
    setContactErrorMessage(null);
    setButtonState('submitting');
    submitWithToken(widgetFormToken);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    handleSubmit();
  };

  const closeConfirmation = () => {
    pendingConfirmationTokenRef.current = null;
    setConfirmation(null);
  };

  const onCustomerConfirmed = (responseData) => {
    storeConfirmedCustomerToken(widgetIdentifier, responseData.confirmed_customer_token);
    closeConfirmation();
    showSuccess();
  };

  // The customer is confirmed, but the request could not become a quote (the owner changed the widget in between):
  // the form shows why, and the corrected request is sent without another code.
  const onConfirmedQuoteError = (responseData) => {
    storeConfirmedCustomerToken(widgetIdentifier, responseData?.confirmed_customer_token);
    closeConfirmation();
    showRequestError(responseData || { error: t('quote_builder_widget.unexpected_error') });
  };

  return (
    <>
    <form ref={formRef} onSubmit={handleFormSubmit}>
      <DrawingOptions
        theme={theme}
        id={id}
        setId={setId}
        drawing={drawing}
        unitOption={unitOption}
        onUnitChange={setUnit}
      />
      {widgetIdentifier && DrawingMaterialChooser && (
        <DrawingMaterialChooser widgetIdentifier={widgetIdentifier} drawingId={id} onCartonMaterialSelected={setCartonMaterialUserPriceId} />
      )}
      {widgetIdentifier && PrintingColorChooser && (
        <PrintingColorChooser widgetIdentifier={widgetIdentifier} cartonMaterialUserPriceId={cartonMaterialUserPriceId} />
      )}
      {widgetIdentifier && HotStampingChooser && (
        <HotStampingChooser widgetIdentifier={widgetIdentifier} cartonMaterialUserPriceId={cartonMaterialUserPriceId} drawingId={id} unit={unit} />
      )}
      {errorMessage && (
        <div
          data-widget-element="error-message"
          style={{
            color: '#dc3545',
            padding: '8px 12px',
            marginBottom: '12px',
            fontSize: '14px'
          }}
          dangerouslySetInnerHTML={{ __html: errorMessage }}
        />
      )}
      {widgetIdentifier && SubmitButton && (
        <SubmitButton
          buttonState={buttonState}
          onClick={handleSubmit}
        />
      )}
    </form>
    {contactPopupOpen && (
      <CustomerContactPopup
        fields={contactFields}
        initialValues={customerContactRef.current}
        sending={buttonState === 'submitting'}
        errorMessage={contactErrorMessage}
        onSend={sendWithContact}
        onClose={closeContactPopup}
        classNames={theme.customerPopupClassNames}
      />
    )}
    {confirmation && (
      <CustomerConfirmationPopup
        widgetIdentifier={widgetIdentifier}
        confirmation={confirmation}
        onConfirmed={onCustomerConfirmed}
        onQuoteError={onConfirmedQuoteError}
        onClose={() => setConfirmation(null)}
        classNames={theme.customerPopupClassNames}
      />
    )}
    </>
  );
};

export default QuoteBuilderForm;
